import { apiClient } from '.';

// 출석 API 관련 타입 정의
export interface AttendanceRecord {
  id: number;
  userId: number;
  lectureId: number;
  attendanceDate: string; // YYYY-MM-DD 형식
  watchedDuration: number; // 시청 시간 (초)
  createdAt: string;
  updatedAt: string;
}

export interface DailyAttendance {
  date: string; // YYYY-MM-DD 형식
  isAttended: boolean;
  totalWatchedDuration: number; // 해당 날짜의 총 시청 시간
  lectureCount: number; // 해당 날짜에 시청한 강의 수
}

export interface MonthlyAttendanceResponse {
  code: number;
  status: string;
  message: string;
  data: {
    year: number;
    month: number;
    attendanceRecords: DailyAttendance[];
    totalAttendanceDays: number;
    attendanceRate: number; // 출석률 (%)
  };
}

export interface AttendanceCreateRequest {
  lectureId: number;
  watchedDuration: number;
  attendanceDate?: string; // 옵션: 기본값은 오늘 날짜
}

// 월별 출석 기록 조회 API
export const getMonthlyAttendance = async (year: number, month: number): Promise<MonthlyAttendanceResponse> => {
  try {
    const response = await apiClient.get<MonthlyAttendanceResponse>(
      '/api/attendance/monthly',
      {
        params: { year, month },
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    /// console.log('응답 object:', response.data);
    return response.data;
  } catch (error) {
    /// console.error('월별 출석 기록 조회 실패:', error);
    // 개발 중에는 더미 데이터 반환
    /// console.log('API 호출 실패, 더미 데이터 반환');
    return getMockMonthlyAttendance(year, month);
  }
};

// 출석 기록 생성/업데이트 API
export const createOrUpdateAttendance = async (data: AttendanceCreateRequest): Promise<{ success: boolean }> => {
  try {
    const response = await apiClient.post<{ code: number }>(
      '/api/attendance',
      data,
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    /// console.log('출석 기록 API 응답:', {response});
    return { success: response.data.code === 200 };
  } catch (error) {
    /// console.error('출석 기록 생성/업데이트 실패:', error);
    return { success: false };
  }
};

// 사용자의 출석 현황 조회 API
export const getUserAttendanceStats = async (): Promise<{
  totalAttendanceDays: number;
  currentMonthAttendance: number;
  attendanceRate: number;
}> => {
  try {
    const response = await apiClient.get<{ data: { totalAttendanceDays: number; currentMonthAttendance: number; attendanceRate: number } }>(
      '/api/attendance/stats',
      {
        withCredentials: true,
        headers: { 'Content-Type': 'application/json' },
      }
    );

    /// console.log('응답 object:', response.data);
    return response.data.data;
  } catch (error) {
    /// console.error('출석 현황 조회 실패:', error);
    // 더미 데이터 반환
    return {
      totalAttendanceDays: 15,
      currentMonthAttendance: 8,
      attendanceRate: 75.5,
    };
  }
};

// 더미 데이터 생성 함수 (개발용)
const getMockMonthlyAttendance = (year: number, month: number): MonthlyAttendanceResponse => {
  const daysInMonth = new Date(year, month, 0).getDate();
  const attendanceRecords: DailyAttendance[] = [];
  
  // 현재 날짜
  const today = new Date();
  const currentDate = today.getDate();
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();
  
  // 랜덤하게 출석 데이터 생성
  for (let day = 1; day <= daysInMonth; day++) {
    const date = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    
    // 미래 날짜는 출석하지 않음
    const isToday = year === currentYear && month === currentMonth && day === currentDate;
    const isFuture = year > currentYear || 
                    (year === currentYear && month > currentMonth) || 
                    (year === currentYear && month === currentMonth && day > currentDate);
    
    if (isFuture && !isToday) {
      continue;
    }
    
    // 80% 확률로 출석
    const isAttended = Math.random() > 0.2;
    
    // 특정 날짜는 확실히 출석으로 설정 (테스트용)
    const forceAttended = [9, 11].includes(day) || isToday;
    
    if (isAttended || forceAttended) {
      attendanceRecords.push({
        date,
        isAttended: true,
        totalWatchedDuration: Math.floor(Math.random() * 3600) + 60, // 1분 ~ 1시간
        lectureCount: Math.floor(Math.random() * 3) + 1 // 1-3개 강의
      });
    }
  }
  
  return {
    code: 200,
    status: 'success',
    message: '월별 출석 기록 조회 성공',
    data: {
      year,
      month,
      attendanceRecords,
      totalAttendanceDays: attendanceRecords.length,
      attendanceRate: (attendanceRecords.length / daysInMonth) * 100
    }
  };
};

// 날짜 유틸리티 함수들
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getToday = (): string => {
  return formatDate(new Date());
};

export const isToday = (dateString: string): boolean => {
  return dateString === getToday();
};
